import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/Context";
import { authAPI } from "../../services/api.util";
import OTPVerificationModal from "../others/OTPVerificationModal";

/**
 * OTPVerificationGuard component that ensures student users verify their phone number
 * Blocks all content access until OTP is verified
 * Works on page refresh by checking localStorage and backend
 */
export default function OTPVerificationGuard({ children }) {
  const { loggedIn, loggedInUser, setLoggedInUser } = useAuthContext();
  const location = useLocation();
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [checkingOTP, setCheckingOTP] = useState(true);
  const [isVerified, setIsVerified] = useState(false);

  // Pages that should be accessible even without OTP verification
  const publicPages = ['/login'];
  const isPublicPage = publicPages.includes(location.pathname);

  const needsPhoneVerification = (user) => {
    if (!user || user.role !== 'student') {
      return false;
    }

    // Email OTP login must remain available for valid users who do not have a phone mapped yet.
    if (!user.mobileNumber) {
      return false;
    }

    return user.isOTPVerified === undefined ||
      user.isOTPVerified === null ||
      user.isOTPVerified === false;
  };

  useEffect(() => {
    const checkOTPVerification = async () => {
      // Only check for student users
      if (!loggedIn || !loggedInUser || loggedInUser.role !== 'student') {
        setCheckingOTP(false);
        setIsVerified(true);
        return;
      }

      const needsVerification = needsPhoneVerification(loggedInUser);

      if (needsVerification) {
        // Try to fetch latest user data from backend to ensure we have current status
        try {
          const response = await authAPI.getProfile();
          const userData = response.data?.user;
          
          if (userData) {
            // Update loggedInUser with latest data
            const updatedUser = {
              ...loggedInUser,
              isOTPVerified: userData.isOTPVerified,
              mobileNumber: userData.mobileNumber
            };
            setLoggedInUser(updatedUser);
            
            // Update localStorage as well
            const userDataFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
            if (userDataFromStorage.token) {
              localStorage.setItem('user', JSON.stringify({
                ...userDataFromStorage,
                isOTPVerified: userData.isOTPVerified,
                mobileNumber: userData.mobileNumber
              }));
            }
            
            const stillNeedsVerification = needsPhoneVerification({
              ...updatedUser,
              role: userData.role || updatedUser.role
            });
            
            if (stillNeedsVerification) {
              setShowOTPModal(true);
              setIsVerified(false);
            } else {
              setIsVerified(true);
            }
          } else {
            // If we can't fetch profile, show modal based on current state
            setShowOTPModal(true);
            setIsVerified(false);
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          // On error, show modal based on current state
          if (needsVerification) {
            setShowOTPModal(true);
            setIsVerified(false);
          } else {
            setIsVerified(true);
          }
        }
      } else {
        setIsVerified(true);
      }
      
      setCheckingOTP(false);
    };

    checkOTPVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, loggedInUser?.role, loggedInUser?.isOTPVerified, loggedInUser?._id]);

  const handleVerificationSuccess = () => {
    setShowOTPModal(false);
    setIsVerified(true);
    // The OTPVerificationModal already updates the context and localStorage
    // Force a re-check to ensure state is updated
    setCheckingOTP(true);
    setTimeout(() => {
      setCheckingOTP(false);
    }, 100);
  };

  // Show loading state while checking
  if (checkingOTP) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "3px solid #e2e8f0",
          borderTop: "3px solid #667eea",
          borderRadius: "50%",
          animation: "checkingSpinner 1s linear infinite"
        }}></div>
        <style>
          {`
            @keyframes checkingSpinner {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Show content with OTP modal overlay if verification is required (except public pages)
  if (loggedIn && loggedInUser?.role === 'student' && !isVerified && !isPublicPage) {
    return (
      <>
        {/* Show main content in background */}
        <div style={{
          pointerEvents: showOTPModal ? 'none' : 'auto',
          opacity: showOTPModal ? 0.6 : 1,
          transition: 'opacity 0.3s ease',
          filter: showOTPModal ? 'blur(1px)' : 'none'
        }}>
          {children}
        </div>
        
        {/* Show OTP modal overlay */}
        {showOTPModal && (
          <OTPVerificationModal
            initialMobileNumber={loggedInUser?.mobileNumber}
            onVerificationSuccess={handleVerificationSuccess}
          />
        )}
      </>
    );
  }

  // Allow content access if verified or not a student
  return (
    <>
      {children}
      {/* Show OTP modal if needed (fallback) */}
      {showOTPModal && loggedIn && loggedInUser?.role === 'student' && (
        <OTPVerificationModal
          initialMobileNumber={loggedInUser?.mobileNumber}
          onVerificationSuccess={handleVerificationSuccess}
        />
      )}
    </>
  );
}
